import React__default, { createContext, useState, useContext, useMemo } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import styled from 'styled-components';
import { useKeyPress } from '../../lib/keypress.js';
import { setTestId } from '../../lib/testId.js';
import { useQueryChildComponents, HappyWebSystemError } from '../base/SystemComponent.js';
import { ButtonIcon } from '../button/ButtonIcon.js';
import { COLOR_PALETTE } from '../colors/colorPalette.js';
import { Heading } from '../heading/Heading.js';
import { Icon } from '../icon/Icon.js';
import { Box } from '../layout/Box.js';
import '../layout/BulletList.js';
import '../layout/Column.js';
import '../layout/Columns.js';
import '../layout/Hidden.js';
import { Inline } from '../layout/Inline.js';
import { Stack } from '../layout/Stack.js';
import '../layout/VisuallyHidden.js';
import { LoadingSpinner } from '../loading/LoadingSpinner.js';
import { Modal, ModalHeader, ModalActions } from '../modal/Modal.js';
import { getColor } from '../styles/stylePropTypes.js';

const BORDER_WIDTH_PX = 4;
const GalleryContext = createContext({
    selectedItem: null
});
const useGallery = ()=>{
    const context = useContext(GalleryContext);
    if (!context) {
        throw new HappyWebSystemError("useGallery", "Missing GalleryContext - a GalleryContext must be set.");
    }
    return context;
};
const GalleryProvider = ({ selectedItem , children  })=>{
    const value = useMemo(()=>({
            selectedItem
        }), [
        selectedItem
    ]);
    return React__default.createElement(GalleryContext.Provider, {
        value: value
    }, children);
};
/**
 * Actions available within the Gallery. Current;y selected item is available via render props.
 *
 * @example
 * <Gallery>
 *    <GalleryActions>
 *        ({ selectedItem }) => {(
 *          <Button onClick={() => handleClick(selectedItem.id)} />
 *        )}
 *    </GalleryActions>
 * </Gallery>
 */ const GalleryActions = ({ children  })=>{
    const context = useGallery();
    if (!!context.selectedItem) {
        return React__default.createElement(Box, {
            width: "fill"
        }, React__default.createElement(Inline, {
            width: "fill",
            align: "center",
            gap: 1
        }, children(context)));
    }
    return null;
};
GalleryActions.dataId = "data-hpy-web-gallery-actions";
GalleryActions.displayName = "GalleryActions";
const ImageBox = styled(Box)`
  max-width: 85rem;
  position: absolute;
  justify-content: center;

  & > img {
    border-radius: 0.5rem;
    height: 100%;
  }
`;
const LoadingBox = styled(Box)`
  justify-content: center;
  align-items: center;
`;
const GalleryItemImage = ({ src , ...props })=>{
    return React__default.createElement(React__default.Fragment, null, React__default.createElement(LoadingBox, {
        height: "fill",
        width: "fill"
    }, React__default.createElement(LoadingSpinner, null)), React__default.createElement(ImageBox, {
        "data-hpy-web-gallery-item-image": true,
        ...props,
        height: "fill",
        borderRadius: "medium"
    }, React__default.createElement("img", {
        src: src,
        alt: ""
    })));
};
GalleryItemImage.dataId = "data-hpy-web-gallery-item-image";
GalleryItemImage.displayName = "GalleryItemImage";
const GalleryPlayer = React__default.lazy(()=>import('./GalleryPlayer.js').then((module)=>({
            default: module.GalleryPlayer
        })));
const VideoBox = styled(Box)`
  max-width: 85rem;

  & .plyr--video {
    --plyr-color-main: ${(props)=>getColor("context", props.theme)};
    width: 100%;
    border-radius: 0.5rem;
  }
`;
const GalleryItemVideo = ({ src , ...props })=>{
    return React__default.createElement(VideoBox, {
        "data-hpy-web-gallery-item-video": true,
        ...props,
        padding: [
            0,
            4
        ],
        height: "fill",
        width: "fill",
        minWidth: 5
    }, React__default.createElement(React__default.Suspense, {
        fallback: React__default.createElement(LoadingBox, {
            height: "fill",
            width: "fill"
        }, React__default.createElement(LoadingSpinner, null))
    }, React__default.createElement(AutoSizer, null, ({ height , width  })=>React__default.createElement(Box, {
            style: {
                height: `${height}px`,
                width: `${width}px`
            }
        }, React__default.createElement(GalleryPlayer, {
            source: {
                type: "video",
                sources: [
                    {
                        src,
                        type: "video/mp4"
                    }
                ]
            }
        })))));
};
GalleryItemVideo.dataId = "data-hpy-web-gallery-item-video";
GalleryItemVideo.displayName = "GalleryItemVideo";
/**
 *  Individual Gallery Item (i.e. Photo/Picture/Video)
 *
 *  @example
 * <Gallery>
 *    {items.map(item => <GalleryItem key={item.key} id={item.id} url={item.url} />)}
 * </Gallery>
 */ const GalleryItem = ({ url , type ="image" , ...props })=>{
    return type === "video" ? React__default.createElement(GalleryItemVideo, {
        src: url,
        ...props
    }) : React__default.createElement(GalleryItemImage, {
        src: url,
        ...props
    });
};
GalleryItem.dataId = "data-hpy-web-gallery-item";
GalleryItem.displayName = "GalleryItem";
const NavButtonIcon = styled(ButtonIcon)`
  ${(props)=>props.$position === "right" && "right: 0"};
  position: absolute;
  top: 50%;
  margin: 1rem;
`;
const BodyBox = styled(Box)`
  position: relative;
  justify-content: center;
`;
const FooterBox = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(10rem, 1fr));
  grid-auto-rows: 10rem;
  grid-gap: 1rem;
`;
const FooterItemImageButton = styled.button`
  background-color: ${(props)=>getColor("contextDark60", props.theme)};
  background-image: url(${(props)=>props.src});
  background-position: center center;
  background-repeat: no-repeat;
  background-size: cover;
  border-radius: 0.5rem;
  overflow: hidden;
  border: ${BORDER_WIDTH_PX}px solid
    ${(props)=>props.isSelected ? COLOR_PALETTE.white : getColor("contextDark10", props.theme)};
  padding: 0;
  width: 100%;

  &:focus,
  &:hover {
    outline: none;
    border: ${BORDER_WIDTH_PX}px solid
      ${(props)=>props.isSelected ? COLOR_PALETTE.white : getColor("context30", props.theme)};
    cursor: pointer;
  }
`;
const FooterItemVideoButton = styled.button`
  background-color: ${(props)=>getColor("contextDark60", props.theme)};
  border: ${BORDER_WIDTH_PX}px solid
    ${(props)=>props.isSelected ? COLOR_PALETTE.white : getColor("contextDark10", props.theme)};
  border-radius: 0.5rem;

  overflow: hidden;

  padding: 0;
  width: 100%;

  &:focus,
  &:hover {
    outline: none;
    border: ${BORDER_WIDTH_PX}px solid
      ${(props)=>props.isSelected ? COLOR_PALETTE.white : getColor("context30", props.theme)};
    cursor: pointer;
  }
`;
/**
 * Gallery Component
 * @example
 *   <Gallery
 *     title={title}
 *   />
 */ const Gallery = ({ title , selectedIndex , testId ="X" , children , ...props })=>{
    const [actions] = useQueryChildComponents(children, [
        GalleryActions
    ]);
    const [itemComps] = useQueryChildComponents(children, [
        GalleryItem
    ]);
    const items = itemComps.map((child)=>({
            id: child.props?.id,
            url: child.props?.url,
            type: child.props?.type
        }));
    const [selectedItemIndex, setSelectedItemIndex] = useState(selectedIndex ?? 0);
    const selectedItem = items[selectedItemIndex];
    useKeyPress("ArrowRight", ()=>{
        if (selectedItemIndex < items.length - 1) {
            setSelectedItemIndex(selectedItemIndex + 1);
        }
    });
    useKeyPress("ArrowLeft", ()=>{
        if (selectedItemIndex > 0) {
            setSelectedItemIndex(selectedItemIndex - 1);
        }
    });
    return React__default.createElement(GalleryProvider, {
        selectedItem: selectedItem
    }, React__default.createElement(Box, {
        padding: [
            0,
            3
        ],
        height: "fill"
    }, React__default.createElement(Stack, {
        "data-hpy-web-gallery": true,
        ...setTestId(testId),
        ...props,
        gap: 2,
        height: "fill",
        align: "center"
    }, React__default.createElement(Box, null, React__default.createElement(Stack, {
        gap: 2
    }, React__default.createElement(Heading, {
        level: "2",
        align: "center"
    }, title), actions)), React__default.createElement(BodyBox, {
        height: "fill",
        width: "fill"
    }, React__default.createElement(GalleryItem, {
        id: selectedItem?.id,
        url: selectedItem?.url,
        type: selectedItem?.type,
        ...setTestId(`Gallery-SelectedItem-${testId}-${selectedItem?.id}`)
    })), React__default.createElement(Box, {
        overflow: "scroll",
        maxWidth: 90,
        width: "fill",
        height: "fill",
        maxHeight: 11
    }, React__default.createElement(FooterBox, {
        height: "fill",
        width: "fill",
        maxHeight: 17
    }, items.length > 1 && items.map((item, index)=>React__default.createElement(Box, {
            key: index,
            ...setTestId(`Gallery-Footer-${testId}`)
        }, item.type !== "video" && React__default.createElement(FooterItemImageButton, {
            key: index,
            src: item.url,
            isSelected: selectedItemIndex === index,
            onClick: ()=>setSelectedItemIndex(index),
            ...setTestId(`Gallery-ItemPreview-${testId}-${item.id}`)
        }), item.type === "video" && React__default.createElement(FooterItemVideoButton, {
            key: index,
            isSelected: selectedItemIndex === index,
            onClick: ()=>setSelectedItemIndex(index),
            ...setTestId(`Gallery-ItemPreview-${testId}-${item.id}`)
        }, React__default.createElement(Icon, {
            icon: "play-circle",
            size: "xlarge",
            color: "slateGrey5"
        })))))))), selectedItemIndex > 0 && React__default.createElement(NavButtonIcon, {
        "$position": "left",
        icon: "arrow-left",
        variant: "context",
        testId: `Gallery-PreviousItem-${testId}`,
        onClick: ()=>setSelectedItemIndex(selectedItemIndex - 1)
    }), selectedItemIndex < items.length - 1 && React__default.createElement(NavButtonIcon, {
        "$position": "right",
        icon: "arrow-right",
        variant: "context",
        testId: `Gallery-NextItem-${testId}`,
        onClick: ()=>setSelectedItemIndex(selectedItemIndex + 1)
    }));
};
Gallery.dataId = "data-hpy-web-gallery";
Gallery.displayName = "Gallery";
const GallerySystemModal = styled(Modal)`
  & [${Modal.dataId}-content] {
    background: transparent;
  }
`;
const GalleryModal = ({ children , ...props })=>{
    const [close, content] = useQueryChildComponents(children, [
        GalleryModalCloseButton
    ]);
    return React__default.createElement(GallerySystemModal, {
        "data-hpy-web-gallery-modal": true,
        type: "fullscreen",
        ...props
    }, React__default.createElement(ModalHeader, null, React__default.createElement(ModalActions, null, close)), content);
};
GalleryModal.dataId = "data-hpy-web-gallery-modal";
GalleryModal.displayName = "GalleryModal";
const GalleryModalCloseButton = ({ testId ="X" , ...props })=>{
    return React__default.createElement(ButtonIcon, {
        "data-hpy-web-gallery-modal-close-button": true,
        icon: "close",
        variant: "context-secondary",
        ...props,
        testId: "GalleryModalClose-" + testId
    });
};
GalleryModalCloseButton.dataId = "data-hpy-web-gallery-modal-close-button";
GalleryModalCloseButton.displayName = "GalleryModalCloseButton";

export { Gallery, GalleryActions, GalleryItem, GalleryModal, GalleryModalCloseButton };
